import React from 'react';
import { Card, Typography, useTheme } from '@mui/material';
import { ApiDefinition, ApiTag } from '../../types/api-definitions';
import { PublicIcon, PrivateIcon } from '../../icons';
import { useNavigate } from 'react-router-dom';
import { useLocalizedContent, useLocalizedFeatures } from '../../hooks/useLocalizedContent';
import { useTranslation } from 'react-i18next';
import Tag from '../common/Tag';
import FeatureDisplay from '../common/FeatureDisplay';
import { StyledIconContainer } from '../common/IconContainer';
import {
  StyledCardContent,
  StyledHeaderBox,
  StyledTitleContainer,
  StyledCompanyLogo,
  StyledTagsContainer,
  StyledFeaturesContainer,
  StyledFeaturesTitle,
  StyledFeaturesList,
  StyledSeeAllContainer,
  StyledSeeAllText,
} from './ApiCard.styles';

interface ApiCardProps {
  apiDefinition: ApiDefinition;
}

const ApiCard: React.FC<ApiCardProps> = ({ apiDefinition: apiDefinition }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const content = useLocalizedContent(apiDefinition)!;
  const features = useLocalizedFeatures(apiDefinition);
  const { t } = useTranslation();

  const handleClick = () => {
    navigate(`/api-definitions/${apiDefinition.slug}`);
  };

  return (
    <Card variant='apiCard' onClick={handleClick}>
      <StyledCardContent>
        <StyledHeaderBox>
          <StyledTitleContainer>
            <Typography variant='h4'>{content.title}</Typography>
            <StyledIconContainer>
              {apiDefinition.isPublic ? <PublicIcon /> : <PrivateIcon />}
            </StyledIconContainer>
          </StyledTitleContainer>
          {apiDefinition.company?.logoPath && (
            <StyledCompanyLogo
              src={apiDefinition.company.logoPath}
              alt={`${apiDefinition.company.name || 'Company'} logo`}
            />
          )}
        </StyledHeaderBox>

        {content.tags && content.tags.length > 0 && (
          <StyledTagsContainer>
            {content.tags.map((tag: ApiTag) => (
              <Tag
                key={tag.slug}
                text={tag.name}
                backgroundColor={theme.palette.tags.lightTagBackground}
                color={theme.palette.tags.lightTagText}
              />
            ))}
          </StyledTagsContainer>
        )}
        <Typography variant='body2' color='text.secondary'>
          {content.descriptionShort}
        </Typography>
        {features && features.length > 0 && (
          <StyledFeaturesContainer>
            <StyledFeaturesTitle variant='body2'>
              {t('apiCard.containingFeatures')}
            </StyledFeaturesTitle>
            <StyledFeaturesList>
              {features.slice(0, 3).map((feature) => (
                <FeatureDisplay
                  key={feature.slug}
                  feature={feature}
                  description={feature.descriptionShort}
                />
              ))}
              {features.length > 3 && (
                <StyledSeeAllContainer>
                  <StyledSeeAllText variant='body2' color='text.secondary'>
                    {t('apiCard.seeAllFeatures', { count: content.features.length })}
                  </StyledSeeAllText>
                </StyledSeeAllContainer>
              )}
            </StyledFeaturesList>
          </StyledFeaturesContainer>
        )}
      </StyledCardContent>
    </Card>
  );
};

export default ApiCard;
